import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subDays } from "date-fns";
import { AlertTriangle, CalendarDays, CalendarPlus, LayoutGrid, Loader2, Package, Plus, QrCode, Search, User, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { AppMainHeader } from "@/components/AppMainHeader";
import { getDotPatternStyle } from "@/components/AppBackground";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import {
  cancelarMaterialReserva,
  getApiErrorMessage,
  getMaterialItems,
  getMaterialReservas,
  getMaterialUsers,
  recogerMaterialReserva,
} from "@/lib/api";
import { MATERIAL_ESTADOS, MATERIAL_ESTADO_ORDEN, formatReservaRango } from "@/lib/material";
import MaterialItemCard from "@/components/material/MaterialItemCard";
import MaterialTimeline from "@/components/material/MaterialTimeline";
import ReservaDialog from "@/components/material/ReservaDialog";
import DevolverDialog from "@/components/material/DevolverDialog";
import ItemFormDialog from "@/components/material/ItemFormDialog";
import ItemSheet from "@/components/material/ItemSheet";
import ReservaRow from "@/components/material/ReservaRow";

const TABS = [
  { id: "inventario", label: "Inventario", icon: LayoutGrid },
  { id: "calendario", label: "Calendario", icon: CalendarDays },
  { id: "mias", label: "Mis reservas", icon: User },
];

function leerTab() {
  try {
    const t = localStorage.getItem("material_tab");
    return TABS.some((x) => x.id === t) ? t : "inventario";
  } catch (_) {
    return "inventario";
  }
}

export default function MaterialPage() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const settings = useSettings();
  const me = (user?.email || "").toLowerCase();

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState(leerTab);
  const [filtro, setFiltro] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [misReservas, setMisReservas] = useState([]);

  const [reservaDlg, setReservaDlg] = useState({ open: false, config: null });
  const [devolverDlg, setDevolverDlg] = useState({ open: false, reserva: null, item: null });
  const [itemForm, setItemForm] = useState({ open: false, item: null });
  const [sheet, setSheet] = useState({ open: false, itemId: null, focusReservaId: null });
  const [cancelando, setCancelando] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.material === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  const puedeVer = !!user && user.permissions?.material !== false;

  const loadItems = useCallback(() => {
    return getMaterialItems()
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(getApiErrorMessage(e, "No se pudo cargar el material")));
  }, []);

  useEffect(() => {
    if (!puedeVer) return;
    setLoading(true);
    Promise.all([
      loadItems(),
      getMaterialUsers()
        .then((d) => {
          setUsers(d.users || []);
          setIsAdmin(!!d.is_admin);
        })
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [puedeVer, loadItems]);

  // Refresco periódico: los estados (retrasado, reservado…) dependen de la hora
  useEffect(() => {
    if (!puedeVer) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") loadItems();
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [puedeVer, loadItems]);

  useEffect(() => {
    if (!puedeVer || tab !== "mias") return;
    getMaterialReservas({ mine: true, desde: subDays(new Date(), 60).toISOString() })
      .then((d) => setMisReservas(d.reservas || []))
      .catch((e) => toast.error(getApiErrorMessage(e, "No se pudieron cargar tus reservas")));
  }, [puedeVer, tab, refreshKey]);

  useEffect(() => {
    try {
      localStorage.setItem("material_tab", tab);
    } catch (_) {
      /* sin almacenamiento */
    }
  }, [tab]);

  const recargar = useCallback(() => {
    loadItems();
    setRefreshKey((k) => k + 1);
  }, [loadItems]);

  const usersByEmail = useMemo(() => Object.fromEntries(users.map((u) => [u.email, u])), [users]);
  const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const ubicaciones = useMemo(() => [...new Set(items.map((i) => i.ubicacion).filter(Boolean))].sort(), [items]);

  const conteo = useMemo(() => {
    const c = Object.fromEntries(MATERIAL_ESTADO_ORDEN.map((e) => [e, 0]));
    items.forEach((i) => {
      c[i.estado] = (c[i.estado] || 0) + 1;
    });
    return c;
  }, [items]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items.filter(
      (i) =>
        (!filtro || i.estado === filtro) &&
        (!q || [i.nombre, i.ubicacion, i.numero_serie, i.descripcion].some((v) => (v || "").toLowerCase().includes(q)))
    );
  }, [items, filtro, busqueda]);

  const misRetrasos = items.filter((i) => i.estado === "retrasado" && i.actual?.user_email === me);

  // ── Acciones ────────────────────────────────────────────────────────────────
  const abrirReserva = (itemIds, extra = {}) => setReservaDlg({ open: true, config: { modo: "reservar", itemIds, ...extra } });
  const abrirCogerAhora = (item) => setReservaDlg({ open: true, config: { modo: "ahora", itemIds: [item.id] } });
  const abrirEditar = (reserva) => setReservaDlg({ open: true, config: { modo: "editar", reserva } });
  const abrirDevolver = (reserva, item) => setDevolverDlg({ open: true, reserva, item: item || itemsById[reserva.item_id] });
  const abrirItem = (item, reserva) => setSheet({ open: true, itemId: item.id, focusReservaId: reserva?.id || null });

  const recoger = async (reserva) => {
    try {
      await recogerMaterialReserva(reserva.id);
      toast.success(`${itemsById[reserva.item_id]?.nombre || "Equipo"} recogido. ¡Buen rodaje!`);
      recargar();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "No se pudo marcar como recogido"));
    }
  };

  const confirmarCancelar = async () => {
    if (!cancelando) return;
    try {
      await cancelarMaterialReserva(cancelando.id);
      toast.success("Reserva cancelada");
      recargar();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "No se pudo cancelar"));
    } finally {
      setCancelando(null);
    }
  };

  const reservaActions = { onRecoger: recoger, onDevolver: abrirDevolver, onCancelar: setCancelando, onEditar: abrirEditar };

  const toggleSeleccion = (id) =>
    setSeleccion((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const onSelectRange = useCallback(
    (itemId, desde, hasta) => setReservaDlg({ open: true, config: { modo: "reservar", itemIds: [itemId], desde, hasta } }),
    []
  );

  if (authLoading || !puedeVer) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const ahoraIso = new Date().toISOString();
  const misEnUso = misReservas.filter((r) => r.estado === "en_uso");
  const misProximas = misReservas.filter((r) => r.estado === "reservada" && r.fin > ahoraIso);
  const misPasadas = misReservas
    .filter((r) => r.estado === "devuelta" || (r.estado === "reservada" && r.fin <= ahoraIso))
    .sort((a, b) => (a.inicio < b.inicio ? 1 : -1))
    .slice(0, 15);
  const sheetItem = sheet.itemId ? itemsById[sheet.itemId] : null;

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <AppMainHeader title="Material" titleAs="h5" />
      <main className="flex-1" style={getDotPatternStyle(settings)}>
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6">
          {/* Cabecera */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="mr-auto">
              <h1 className="text-2xl font-bold tracking-tight">Material de grabación</h1>
              <p className="text-sm text-muted-foreground">Reserva, recoge y devuelve. Así siempre sabemos dónde está cada cosa.</p>
            </div>
            {isAdmin && items.length > 0 && (
              <Button variant="outline" onClick={() => navigate("/material/etiquetas")} className="gap-1.5 bg-background">
                <QrCode className="h-4 w-4" /> Etiquetas QR
              </Button>
            )}
            {isAdmin && (
              <Button onClick={() => setItemForm({ open: true, item: null })} className="gap-1.5">
                <Plus className="h-4 w-4" /> Nuevo equipo
              </Button>
            )}
          </div>

          {/* KPIs de estado */}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
            {MATERIAL_ESTADO_ORDEN.map((e) => {
              const cfg = MATERIAL_ESTADOS[e];
              const activo = filtro === e;
              return (
                <button
                  key={e}
                  type="button"
                  onClick={() => {
                    setFiltro(activo ? null : e);
                    if (tab === "mias") setTab("inventario");
                  }}
                  className={cn(
                    "relative flex flex-col items-start rounded-xl border bg-gradient-to-br px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md",
                    cfg.kpi,
                    activo && "ring-2 ring-offset-2 ring-offset-background " + cfg.ring,
                    filtro && !activo && "opacity-55",
                    e === "retrasado" && conteo[e] === 0 && "opacity-60"
                  )}
                >
                  <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide opacity-80">
                    <span className={cn("h-2 w-2 rounded-full", cfg.dot, e === "retrasado" && conteo[e] > 0 && "animate-pulse")} />
                    {cfg.label}
                  </span>
                  <span className="mt-1 text-3xl font-bold tabular-nums">{loading ? "–" : conteo[e]}</span>
                  {activo && <X className="absolute right-2.5 top-2.5 h-3.5 w-3.5 opacity-60" />}
                </button>
              );
            })}
          </div>

          {misRetrasos.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span className="flex-1">
                Tienes <strong>{misRetrasos.length}</strong> {misRetrasos.length === 1 ? "equipo pendiente" : "equipos pendientes"} de devolver:{" "}
                {misRetrasos.map((i) => i.nombre).join(", ")}
              </span>
              <Button size="sm" variant="destructive" onClick={() => abrirDevolver(misRetrasos[0].actual, misRetrasos[0])}>
                Devolver ahora
              </Button>
            </div>
          )}

          {/* Pestañas + búsqueda */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex rounded-xl border bg-muted p-1 shadow-sm">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                    tab === id ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
            {tab !== "mias" && (
              <div className="relative ml-auto w-full sm:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar equipo o ubicación…" className="pl-9 bg-background" />
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed bg-card/60 px-6 py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Package className="h-8 w-8" strokeWidth={1.5} />
              </div>
              <h2 className="text-lg font-semibold">Aún no hay material en el inventario</h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                {isAdmin ? "Da de alta cámaras, ópticas, micros, luces… con su foto y dónde se guardan." : "Pide a un administrador que dé de alta el material."}
              </p>
              {isAdmin && (
                <Button onClick={() => setItemForm({ open: true, item: null })} className="mt-2 gap-1.5">
                  <Plus className="h-4 w-4" /> Añadir el primer equipo
                </Button>
              )}
            </div>
          ) : tab === "inventario" ? (
            visibles.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">Ningún equipo coincide con el filtro.</p>
            ) : (
              <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {visibles.map((item) => (
                  <MaterialItemCard
                    key={item.id}
                    item={item}
                    me={me}
                    usersByEmail={usersByEmail}
                    selected={seleccion.has(item.id)}
                    onToggleSelect={toggleSeleccion}
                    onOpen={abrirItem}
                    onReservar={abrirReserva}
                    onCogerAhora={abrirCogerAhora}
                    onRecoger={recoger}
                    onDevolver={abrirDevolver}
                  />
                ))}
              </div>
            )
          ) : tab === "calendario" ? (
            <MaterialTimeline
              items={visibles}
              usersByEmail={usersByEmail}
              me={me}
              refreshKey={refreshKey}
              onSelectRange={onSelectRange}
              onOpenItem={abrirItem}
            />
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <section className="flex flex-col gap-2">
                <h2 className="text-sm font-semibold">En tu poder</h2>
                {misEnUso.length ? (
                  misEnUso.map((r) => <ReservaRow key={r.id} reserva={r} item={itemsById[r.item_id]} showItem me={me} isAdmin={isAdmin} usersByEmail={usersByEmail} {...reservaActions} />)
                ) : (
                  <p className="rounded-xl border border-dashed bg-card px-4 py-6 text-center text-sm text-muted-foreground">No tienes nada ahora mismo 👌</p>
                )}
                <h2 className="mt-4 text-sm font-semibold">Próximas reservas</h2>
                {misProximas.length ? (
                  misProximas.map((r) => <ReservaRow key={r.id} reserva={r} item={itemsById[r.item_id]} showItem me={me} isAdmin={isAdmin} usersByEmail={usersByEmail} {...reservaActions} />)
                ) : (
                  <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed bg-card px-4 py-6 text-center text-sm text-muted-foreground">
                    No tienes reservas próximas
                    <Button size="sm" variant="outline" className="gap-1.5 bg-background" onClick={() => setTab("calendario")}>
                      <CalendarPlus className="h-4 w-4" /> Ir al calendario
                    </Button>
                  </div>
                )}
              </section>
              <section className="flex flex-col gap-2">
                <h2 className="text-sm font-semibold">Historial reciente</h2>
                {misPasadas.length ? (
                  misPasadas.map((r) => <ReservaRow key={r.id} reserva={r} item={itemsById[r.item_id]} showItem me={me} isAdmin={isAdmin} usersByEmail={usersByEmail} {...reservaActions} />)
                ) : (
                  <p className="text-sm text-muted-foreground">Sin movimientos en los últimos 60 días.</p>
                )}
              </section>
            </div>
          )}
        </div>
      </main>

      {/* Barra de selección múltiple */}
      {seleccion.size > 0 && tab === "inventario" && (
        <div className="fixed inset-x-0 bottom-5 z-40 flex justify-center px-4">
          <div className="flex items-center gap-3 rounded-full border bg-foreground py-2 pl-5 pr-2 text-background shadow-2xl">
            <span className="text-sm font-medium">
              {seleccion.size} {seleccion.size === 1 ? "equipo seleccionado" : "equipos seleccionados"}
            </span>
            <Button size="sm" variant="secondary" className="gap-1.5 rounded-full" onClick={() => abrirReserva([...seleccion])}>
              <CalendarPlus className="h-4 w-4" /> Reservar juntos
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-background hover:bg-background/10 hover:text-background" onClick={() => setSeleccion(new Set())} aria-label="Limpiar selección">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <ReservaDialog
        open={reservaDlg.open}
        onOpenChange={(open) => setReservaDlg((s) => ({ ...s, open }))}
        config={reservaDlg.config}
        items={items}
        usersByEmail={usersByEmail}
        onDone={() => {
          setSeleccion(new Set());
          recargar();
        }}
      />
      <DevolverDialog
        open={devolverDlg.open}
        onOpenChange={(open) => setDevolverDlg((s) => ({ ...s, open }))}
        reserva={devolverDlg.reserva}
        item={devolverDlg.item}
        onDone={recargar}
      />
      <ItemFormDialog
        open={itemForm.open}
        onOpenChange={(open) => setItemForm((s) => ({ ...s, open }))}
        item={itemForm.item}
        ubicaciones={ubicaciones}
        onDone={recargar}
      />
      <ItemSheet
        open={sheet.open && !!sheetItem}
        onOpenChange={(open) => setSheet((s) => ({ ...s, open }))}
        item={sheetItem}
        focusReservaId={sheet.focusReservaId}
        me={me}
        isAdmin={isAdmin}
        usersByEmail={usersByEmail}
        refreshKey={refreshKey}
        onReservar={abrirReserva}
        onCogerAhora={abrirCogerAhora}
        onEditarItem={(item) => setItemForm({ open: true, item })}
        onChanged={recargar}
        reservaActions={reservaActions}
      />

      <AlertDialog open={!!cancelando} onOpenChange={(o) => !o && setCancelando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar la reserva?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelando && `${itemsById[cancelando.item_id]?.nombre || "Equipo"} · ${formatReservaRango(cancelando)}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarCancelar} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Cancelar reserva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

